import { RESET_SELECTION } from "~/features/configurator/common/advancedTranslationUtils";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import * as UnitCstics from "~/features/configurator/Linear/v2/configuration/kllt/KLLTUnitCstics";
import * as CarriageCstics from "~/features/configurator/Carriage/v2/configuration/kwlt/KWLTCarriageCstics";
import type { ConfiguratorDefinition } from "~/features/configurator/common/v2/StepDefinition";
import { URL_CONTACT_US } from "~/utils/url/staticUrls";
import { csticsHaveValue, getCsticNumericValue, getCsticsValue } from "~/features/configurator/common/v2/helper";
import {
  type AlarHelperTextFactoryParams,
  ExtraConfig,
  HelperCstics,
  InputCstics,
} from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/constants";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import {
  CsticNames,
  ExtraConfig as CarriageExtraConfig,
} from "~/features/configurator/Linear/components/Substeps/CarriageDetailsSubstep/constants";
import { userIsCustomer } from "~/domains/user/utils/roles";

export const KLLTUnitConfiguratorDefinition: ConfiguratorDefinition = {
  description: "configurator.unit.cstic.systemLayout.description",
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
        return materialName === "S1W1";
      },
      value: "configurator.unit.cstic.systemLayout.tile.1.1.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
        return materialName === "S1W2";
      },
      value: "configurator.unit.cstic.systemLayout.tile.1.2.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
        return materialName === "S1W3";
      },
      value: "configurator.unit.cstic.systemLayout.tile.1.3.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
        return materialName === "S2W1";
      },
      value: "configurator.unit.cstic.systemLayout.tile.2.1.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
        return materialName === "S2W2";
      },
      value: "configurator.unit.cstic.systemLayout.tile.2.2.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
        return materialName === "S2W3";
      },
      value: "configurator.unit.cstic.systemLayout.tile.2.3.imageUrl",
    },
  ],
  pages: [
    {
      name: "configurator.unit.page.1.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SystemLayout,
          name: "configurator.unit.cstic.systemLayout.name",
          description: "configurator.unit.cstic.systemLayout.description",
          info: "configurator.unit.cstic.systemLayout.info",
          tiles: [
            {
              name: "configurator.unit.cstic.systemLayout.tile.1.1.name",
              imageUrl: "configurator.unit.cstic.systemLayout.tile.1.1.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.MaterialName,
                  value: "S1W1",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.systemLayout.tile.1.2.name",
              imageUrl: "configurator.unit.cstic.systemLayout.tile.1.2.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.MaterialName,
                  value: "S1W2",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.systemLayout.tile.1.3.name",
              imageUrl: "configurator.unit.cstic.systemLayout.tile.1.3.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.MaterialName,
                  value: "S1W3",
                },
              ],
            },
            // {
            //   name: "configurator.unit.cstic.systemLayout.tile.2.1.name",
            //   imageUrl: "configurator.unit.cstic.systemLayout.tile.2.1.imageUrl",
            //   cstics: [
            //     {
            //       csticName: UnitCstics.MaterialName,
            //       value: "S2W1",
            //     },
            //   ],
            // },
            // {
            //   name: "configurator.unit.cstic.systemLayout.tile.2.2.name",
            //   imageUrl: "configurator.unit.cstic.systemLayout.tile.2.2.imageUrl",
            //   cstics: [
            //     {
            //       csticName: UnitCstics.MaterialName,
            //       value: "S2W2",
            //     },
            //   ],
            // },
            // {
            //   name: "configurator.unit.cstic.systemLayout.tile.2.3.name",
            //   imageUrl: "configurator.unit.cstic.systemLayout.tile.2.3.imageUrl",
            //   cstics: [
            //     {
            //       csticName: UnitCstics.MaterialName,
            //       value: "S2W3",
            //     },
            //   ],
            // },
          ],
          tilesPerRow: 3,

          csticsToReset: [UnitCstics.MaterialName],

          visible: true,

          extraTexts: {
            helpTileName: "configurator.unit.cstic.systemLayout.helpTile.name",
            helpTileUrl: URL_CONTACT_US,
            helpTileImageUrl: "https://cdn.schaeffler-ecommerce.com/frontend/linear/linear_1_1_contact.png",
          },
        },
        {
          type: SubstepType.Size,
          name: "configurator.unit.cstic.size.name",
          description: "configurator.unit.cstic.size.description",
          tiles: [
            {
              name: "configurator.unit.cstic.sizeCode.15.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "15",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.20.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "20",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.25.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "25",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.30.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "30",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.35.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "35",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.45.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "45",
                },
              ],
            },
          ],
          tilesPerRow: 12,
          csticsToReset: [UnitCstics.SizeCode],
          visible: true,
        },
        {
          type: SubstepType.Preload,
          name: "configurator.unit.cstic.preload.name",
          description: "configurator.unit.cstic.preload.description",
          info: "configurator.unit.cstic.preload.info",
          tiles: [
            {
              name: "configurator.unit.cstic.preload.V0.name",
              cstics: [
                {
                  csticName: UnitCstics.Preload,
                  value: "V0",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.preload.V1.name",
              cstics: [
                {
                  csticName: UnitCstics.Preload,
                  value: "V1",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.preload.V2.name",
              cstics: [
                {
                  csticName: UnitCstics.Preload,
                  value: "V2",
                },
              ],
            },
          ],
          tilesPerRow: 6,
          csticsToReset: [UnitCstics.Preload],
          visible: true,
        },
        {
          type: SubstepType.Accuracy,
          name: "configurator.unit.cstic.accuracy.name",
          description: "configurator.unit.cstic.accuracy.description",
          info: "configurator.unit.cstic.accuracy.info",
          visible: true,
          csticsToReset: [UnitCstics.Accuracy],
          tiles: [
            {
              name: "configurator.unit.cstic.accuracy.G1.name",
              cstics: [
                {
                  csticName: UnitCstics.Accuracy,
                  value: "G1",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.accuracy.G2.name",
              cstics: [
                {
                  csticName: UnitCstics.Accuracy,
                  value: "G2",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.accuracy.G3.name",
              cstics: [
                {
                  csticName: UnitCstics.Accuracy,
                  value: "G3",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.accuracy.G4.name",
              cstics: [
                {
                  csticName: UnitCstics.Accuracy,
                  value: "G4",
                },
              ],
            },
          ],
          tilesPerRow: 6,
        },
        {
          type: SubstepType.SystemCoating,
          name: "configurator.unit.cstic.coating.name",
          description: "configurator.unit.cstic.coating.description",
          info: "configurator.unit.cstic.coating.info",
          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [materialName] = getCsticsValue(mainKmat, UnitCstics.MaterialName);
                  return ["KUVE", "KUVE..-W"].includes(materialName);
                },
                value: "configurator.unit.cstic.coating.footerInfo",
              },
            ],
          ],
          tiles: [
            {
              name: "configurator.unit.cstic.coating./.name",
              cstics: [
                {
                  csticName: UnitCstics.Coating,
                  value: "NONE",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [UnitCstics.Coating],

          visible: true,
        },
        {
          type: SubstepType.SimpleLubrication,
          name: "configurator.unit.cstic.lubrication.name",
          description: "configurator.unit.cstic.lubrication.description",
          info: "configurator.unit.cstic.lubrication.info",
          tiles: [
            {
              name: "configurator.unit.cstic.lubrication.basic.name",
              description: "configurator.unit.cstic.lubrication.basic.description",
              cstics: [
                {
                  csticName: UnitCstics.GreasingPerGreaseGroup,
                  value: "STD",
                },
              ],
              visible: true,
            },
          ],
          tilesPerRow: 4,
          csticsToReset: [UnitCstics.GreasingPerGreaseGroup],
          visible: true,
          extraConfig: {
            hasDropdown: false,
          },
        },
      ],
    },
    {
      name: "configurator.unit.page.2.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.Attachment,
          name: "configurator.unit.cstic.mounting.name",
          tiles: [
            {
              name: "configurator.unit.cstic.mounting./.name",
              info: "configurator.unit.cstic.mounting./.info",
              imageUrl: "configurator.unit.cstic.mounting./.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.MountingHoleType,
                  value: "O",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.mounting.U.name",
              info: "configurator.unit.cstic.mounting.U.info",
              imageUrl: "configurator.unit.cstic.mounting.U.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.MountingHoleType,
                  value: "U",
                },
              ],
            },
          ],
          csticsToReset: [UnitCstics.MountingHoleType],
          tilesPerRow: 4,
          visible: true,
        },
        {
          type: SubstepType.Closure,
          name: "configurator.unit.cstic.closure.name",
          info: "configurator.unit.cstic.closure.info",

          tilesPerRow: 6,
          csticsToReset: [UnitCstics.ClosingPlugs],

          visible: [
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[UnitCstics.MountingHoleType]?.value !== "U";
              },
              value: true,
            },
          ],

          groupedTiles: [
            {
              name: "configurator.unit.cstic.closure.section.plugs.name",
              visible: true,
              tiles: [
                {
                  name: "configurator.unit.cstic.closure.KA-TN/B.name",
                  description: "configurator.unit.cstic.closure.KA-TN/B.description",
                  imageUrl: "configurator.unit.cstic.closure.KA-TN/B.imageUrl",
                  info: "configurator.unit.cstic.closure.KA-TN/B.info",
                  cstics: [
                    {
                      csticName: UnitCstics.ClosingPlugs,
                      value: "K3",
                    },
                  ],
                },
                {
                  name: "configurator.unit.cstic.closure.KA-M/A.name",
                  description: "configurator.unit.cstic.closure.KA-M/A.description",
                  imageUrl: "configurator.unit.cstic.closure.KA-M/A.imageUrl",
                  info: "configurator.unit.cstic.closure.KA-M/A.info",
                  cstics: [
                    {
                      csticName: UnitCstics.ClosingPlugs,
                      value: "M2",
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          type: SubstepType.LocatingFace,
          name: "configurator.unit.cstic.locatingFace.name",
          description: "configurator.unit.cstic.locatingFace.description",
          info: "configurator.unit.cstic.locatingFace.info",
          visible: true,
          csticsToReset: [UnitCstics.RailLocatingFaceSegment1, UnitCstics.CarriageLocatingFaceSegment1],
          groupedTiles: [
            {
              name: "rail 1",
              visible: true,
              tiles: [
                {
                  name: "configurator.unit.cstic.railLocatingFace.O.name",
                  cstics: [
                    {
                      csticName: UnitCstics.RailLocatingFaceSegment1,
                      value: "O",
                    },
                  ],
                },

                // {
                //   name: "configurator.unit.cstic.railLocatingFace.O.name",
                //   cstics: [
                //     {
                //       csticName: UnitCstics.RailLocatingFaceSegment1,
                //       value: "U",
                //     },
                //   ],
                // },
                {
                  name: "configurator.unit.cstic.carriageLocatingFace.O.name",
                  cstics: [
                    {
                      csticName: UnitCstics.CarriageLocatingFaceSegment1,
                      value: "O",
                    },
                  ],
                },
                // {
                //   name: "configurator.unit.cstic.carriageLocatingFace.O.name",
                //   cstics: [
                //     {
                //       csticName: UnitCstics.CarriageLocatingFaceSegment1,
                //       value: "U",
                //     },
                //   ],
                // },
              ],
            },
            {
              name: "rail 2",
              visible: [
                {
                  condition: ({ subKmats }) => subKmats.filter((kmat) => kmat.type === KmatType.SubRail).length === 2,
                  value: true,
                },
                false,
              ],
              tiles: [
                {
                  name: "configurator.unit.cstic.railLocatingFace.O.name",
                  cstics: [
                    {
                      csticName: UnitCstics.RailLocatingFaceSegment2,
                      value: "O",
                    },
                  ],
                },
                // {
                //   name: "configurator.unit.cstic.railLocatingFace.U.name",
                //   cstics: [
                //     {
                //       csticName: UnitCstics.RailLocatingFaceSegment2,
                //       value: "U",
                //     },
                //   ],
                // },
                {
                  name: "configurator.unit.cstic.carriageLocatingFace.O.name",
                  cstics: [
                    {
                      csticName: UnitCstics.CarriageLocatingFaceSegment2,
                      value: "O",
                    },
                  ],
                },
                // {
                //   name: "configurator.unit.cstic.carriageLocatingFace.U.name",
                //   cstics: [
                //     {
                //       csticName: UnitCstics.CarriageLocatingFaceSegment2,
                //       value: "U",
                //     },
                //   ],
                // },
              ],
            },
          ],
        },
        {
          type: SubstepType.KLLTGuidewaySet,
          visible: true,
          kmatType: KmatType.SubRail,

          name: "configurator.unit.cstic.guidewaySet.name",

          extraTexts: {
            upperMessage: "configurator.unit.cstic.guidewaySet.infoMessage.upper",
            lowerMessage: "configurator.unit.cstic.guidewaySet.infoMessage.lower",
            lengthName: "configurator.unit.cstic.guidewayLength.name", // TODO - Move to cstic
            lengthInfo: "configurator.unit.cstic.guidewayLength.info", // TODO - Move to cstic
            alarName: "configurator.unit.section.alar.name",
            alarInfo: "configurator.unit.section.alar.info",
            alarRecommended: "configurator.unit.section.alar.recommended",
            alarWarningMessage: "configurator.unit.section.alar.warning",
            partsSectionName: "configurator.unit.section.parts.name",
            partsSectionInfo: "configurator.unit.section.parts.info",
          },

          inputCstics: {
            [InputCstics.TotalLength]: {
              name: UnitCstics.GuidewayLength,
              kmatType: KmatType.SubRail,
              required: true,
            },
            [InputCstics.aL]: {
              name: UnitCstics.AL,
              kmatType: KmatType.SubRail,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat, currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.aR]: {
              name: UnitCstics.AR,
              kmatType: KmatType.SubRail,
              disabled: true,
              visible: [
                {
                  condition: ({ mainKmat, currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partSizingType]: {
              name: UnitCstics.PartSizing,
              kmatType: KmatType.SubRail,
              disabled: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partAmount]: {
              name: UnitCstics.PartAmount,
              kmatType: KmatType.SubRail,
              disabled: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            // [InputCstics.partOne]: {
            //   name: UnitCstics.PartOneLength,
            //   required: true,
            //   visible: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         const [partSizingType] = getCsticsValue(mainKmat, UnitCstics.PartSizing);
            //         return partSizingType === "J" && getCsticNumericValue(mainKmat, UnitCstics.PartAmount) >= 2;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            // },
            // [InputCstics.partTwo]: {
            //   name: UnitCstics.PartTwoLength,
            //   required: true,
            //   visible: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         const [partSizingType] = getCsticsValue(mainKmat, UnitCstics.PartSizing);
            //         return partSizingType === "J" && getCsticNumericValue(mainKmat, UnitCstics.PartAmount) >= 2;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            //   disabled: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         return getCsticNumericValue(mainKmat, UnitCstics.PartAmount) === 2;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            // },
            // [InputCstics.partThree]: {
            //   name: UnitCstics.PartThreeLength,
            //   required: true,
            //   visible: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         const [partSizingType] = getCsticsValue(mainKmat, UnitCstics.PartSizing);
            //         return partSizingType === "J" && getCsticNumericValue(mainKmat, UnitCstics.PartAmount) >= 3;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            //   disabled: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         return getCsticNumericValue(mainKmat, UnitCstics.PartAmount) === 3;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            // },
            // [InputCstics.partFour]: {
            //   name: UnitCstics.PartFourLength,
            //   required: true,
            //   visible: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         const [partSizingType] = getCsticsValue(mainKmat, UnitCstics.PartSizing);
            //         return partSizingType === "J" && getCsticNumericValue(mainKmat, UnitCstics.PartAmount) >= 4;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            //   disabled: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         return getCsticNumericValue(mainKmat, UnitCstics.PartAmount) === 4;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            // },
            // [InputCstics.partFive]: {
            //   name: UnitCstics.PartFiveLength,
            //   required: true,
            //   visible: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         const [partSizingType] = getCsticsValue(mainKmat, UnitCstics.PartSizing);
            //         return partSizingType === "J" && getCsticNumericValue(mainKmat, UnitCstics.PartAmount) >= 5;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            //   disabled: [
            //     {
            //       condition: ({ mainKmat }) => {
            //         return getCsticNumericValue(mainKmat, UnitCstics.PartAmount) === 5;
            //       },
            //       value: true,
            //     },
            //     {
            //       condition: () => true,
            //       value: false,
            //     },
            //   ],
            // },
          },

          helperCstics: {
            [HelperCstics.totalLengthMin]: {
              name: UnitCstics.GuidewayLengthMin,
            },
            [HelperCstics.totalLengthMax]: {
              name: UnitCstics.GuidewayLengthMax,
            },
            [HelperCstics.alHardMax]: {
              name: UnitCstics.AlarHardMax,
            },
            [HelperCstics.arHardMax]: {
              name: UnitCstics.AlarHardMax,
            },
            [HelperCstics.alRecommendedMin]: {
              name: UnitCstics.AlarMinRecommended,
            },
            [HelperCstics.alRecommendedMax]: {
              name: UnitCstics.AlarMaxRecommended,
            },
            [HelperCstics.arRecommendedMin]: {
              name: UnitCstics.AlarMinRecommended,
            },
            [HelperCstics.arRecommendedMax]: {
              name: UnitCstics.AlarMaxRecommended,
            },
          },

          extraConfig: {
            [ExtraConfig.AlarHelperTextFactory]: ({
              hardMin = 0,
              hardMax = 0,
              recommendedMin = 0,
              recommendedMax = 0,
              t,
              value,
            }: AlarHelperTextFactoryParams) => {
              if (value === null) {
                return "";
              }
              if (value <= hardMin) {
                return "<=0mm";
              }
              if (value >= hardMax) {
                return `>=${hardMax}mm`;
              }

              if (value < recommendedMin || value > recommendedMax) {
                return (
                  `${t("common.recommended")}\n` +
                  `${t("common.min")} ${recommendedMin} / ${t("common.max")} ${recommendedMax}`
                );
              }

              return "";
            },
            [ExtraConfig.showNotRecommendedWarning]: true,
          },

          csticsToReset: [UnitCstics.GuidewayLength, UnitCstics.AL],
        },
      ],
    },
    {
      name: "configurator.unit.page.3.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.ReferenceCarriage,
          visible: true,
          name: "configurator.unit.step.referenceCarriage.name",
          description: "configurator.unit.step.referenceCarriage.description",
          info: "configurator.unit.cstic.referenceCarriage.info",

          groupedTiles: [
            {
              tiles: [
                {
                  name: "configurator.unit.cstic.carriageType.A.name",
                  imageUrl: "configurator.unit.cstic.carriageType.A.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "A",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.AL.name",
                  imageUrl: "configurator.unit.cstic.carriageType.AL.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "AL",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },

            {
              tiles: [
                {
                  name: "configurator.unit.cstic.carriageType.S.name",
                  imageUrl: "configurator.unit.cstic.carriageType.S.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "S",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.SL.name",
                  imageUrl: "configurator.unit.cstic.carriageType.SL.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "SL",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.H.name",
                  imageUrl: "configurator.unit.cstic.carriageType.H.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "H",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.HL.name",
                  imageUrl: "configurator.unit.cstic.carriageType.HL.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "HL",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },

            {
              tiles: [
                {
                  name: "configurator.unit.cstic.carriageType.AS.name",
                  imageUrl: "configurator.unit.cstic.carriageType.AS.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "AS",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.US.name",
                  imageUrl: "configurator.unit.cstic.carriageType.US.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "US",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.ESC.name",
                  imageUrl: "configurator.unit.cstic.carriageType.ESC.image",
                  cstics: [
                    {
                      // csticName: CarriageCstics.CarriageDesign,
                      csticName: UnitCstics.ReferenceCarriageDesign,
                      value: "ESC",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [
            // CarriageCstics.CarriageDesign
            UnitCstics.ReferenceCarriageDesign,
          ],
        },

        {
          type: SubstepType.KLLTCarriageDetails,
          kmatType: KmatType.SubCarriage,
          visible: true,

          name: "configurator.unit.cstic.configureCarriage.name",
          info: "configurator.unit.cstic.configureCarriage.info",

          extraTexts: {
            sealingAndLubricationSectionName:
              "configurator.unit.cstic.configureCarriage.section.sealingAndLubrication.name",
            sealingAndLubricationSectionInfo:
              "configurator.unit.cstic.configureCarriage.section.sealingAndLubrication.info",
            lubricationConnectorSectionName: "configurator.unit.cstic.lubricationConnector.name",
            lubricationConnectorSectionInfo:
              "configurator.unit.cstic.configureCarriage.section.lubricationConnector.info",
            lubricationConnectorManualSectionName:
              "configurator.unit.cstic.configureCarriage.section.lubricationConnector.manual.name",
            lubricationConnectorCentralSectionName:
              "configurator.unit.cstic.configureCarriage.section.lubricationConnector.central.name",
            edit: "configurator.unit.cstic.configureCarriage.btn.edit",
          },

          inputCstics: {
            [CsticNames.CarriageDesign]: {
              name: CarriageCstics.CarriageDesign,
              kmatType: KmatType.SubCarriage,
              required: true,
              disabled: [
                {
                  condition: ({ currentKmat }) => getCsticsValue(currentKmat, CarriageCstics.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.KITLeft]: {
              name: CarriageCstics.KitsLeft,
              kmatType: KmatType.SubCarriage,
              required: true,
              disabled: [
                {
                  condition: ({ currentKmat }) => getCsticsValue(currentKmat, CarriageCstics.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.KITRight]: {
              name: CarriageCstics.KitsRight,
              kmatType: KmatType.SubCarriage,
              required: true,
              disabled: true,
            },
            [CsticNames.KITCenter]: {
              name: CarriageCstics.KitsCenter,
              kmatType: KmatType.SubCarriage,
              required: true,
              // SCHAEFF-36818 - make kit center always visible as per client request. Changing this again will be a CR.
              // visible: [
              //   {
              //     condition: ({ mainKmat }) => {
              //       return csticHasValue(mainKmat, CarriageCstics.KitsRight);
              //     },
              //     value: true,
              //   },
              //   false,
              // ],
              disabled: [
                {
                  condition: ({ currentKmat }) => getCsticsValue(currentKmat, CarriageCstics.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnectorOrientation]: {
              name: CarriageCstics.LubricationConnectorOrientation,
              kmatType: KmatType.SubCarriage,
              required: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    return csticsHaveValue(
                      currentKmat,
                      CarriageCstics.KitsLeft,
                      CarriageCstics.KitsCenter,
                      CarriageCstics.KitsRight,
                    );
                  },
                  value: true,
                },
                false,
              ],
              disabled: [
                {
                  condition: ({ currentKmat }) => getCsticsValue(currentKmat, CarriageCstics.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnectorPosition]: {
              name: CarriageCstics.LubricationConnectorPosition,
              kmatType: KmatType.SubCarriage,
              required: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    const [connectorOrientation] = getCsticsValue(
                      currentKmat,
                      CarriageCstics.LubricationConnectorOrientation,
                    );
                    return ["L", "R"].includes(connectorOrientation);
                  },
                  value: true,
                },
                false,
              ],
              disabled: [
                {
                  condition: ({ currentKmat }) => getCsticsValue(currentKmat, CarriageCstics.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnector]: {
              name: CarriageCstics.LubricationConnector,
              kmatType: KmatType.SubCarriage,
              required: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    const [lubricationConnectorStatus] = getCsticsValue(
                      currentKmat,
                      CarriageCstics.LubricationConnectorOrientation,
                    );
                    return ["L", "R", "BL", "BR"].includes(lubricationConnectorStatus);
                  },
                  value: true,
                },
                false,
              ],
              disabled: [
                {
                  condition: ({ currentKmat }) => getCsticsValue(currentKmat, CarriageCstics.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnectorAlignment]: {
              name: CarriageCstics.LubricationConnectorAlignment,
              kmatType: KmatType.SubCarriage,
              required: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    const [lubricationConnectorStatus] = getCsticsValue(
                      currentKmat,
                      CarriageCstics.LubricationConnectorOrientation,
                    );
                    return ["L", "R"].includes(lubricationConnectorStatus);
                  },
                  value: true,
                },
                false,
              ],
            },
          },

          helperCstics: {
            [CsticNames.StandardKits]: {
              name: CarriageCstics.StandardKits,
            },
            [CsticNames.LubricationConnectorType]: {
              name: CarriageCstics.LubricationConnectorType,
              kmatType: KmatType.SubCarriage,
            },
            [CsticNames.EnableCarriageEditButton]: {
              name: CarriageCstics.CarriageDesign,
            },
          },

          extraConfig: {
            [CarriageExtraConfig.carriageDesignEditable]: false,
            [CarriageExtraConfig.singleCarriageMode]: true,
          },

          csticsToReset: [
            CarriageCstics.StandardKits,
            CarriageCstics.KitsLeft,
            CarriageCstics.KitsRight,
            CarriageCstics.KitsCenter,
            CarriageCstics.LubricationConnectorPosition,
            CarriageCstics.LubricationConnector,
            CarriageCstics.LubricationConnectorOrientation,
            CarriageCstics.LubricationConnectorAlignment,
          ],
        },
      ],
    },
    {
      name: "configurator.unit.page.4.name",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,

          info: [
            {
              condition: () => true,
              value: ({ mainKmat, subKmats }) => {
                const userPart = userIsCustomer() ? "business" : "plus";
                const railAmount = subKmats.filter((kmat) => kmat.type === KmatType.SubRail).length;

                const [locatingFaceSegment1, locatingFaceSegment2] = getCsticsValue(
                  mainKmat,
                  UnitCstics.RailLocatingFaceSegment1,
                  UnitCstics.RailLocatingFaceSegment2,
                );
                const isBelow = [locatingFaceSegment1, railAmount === 2 ? locatingFaceSegment2 : undefined].some(
                  (segment) => segment === "U",
                );
                const sidePart = isBelow ? "below" : "above";
                return `configurator.unit.summary.info.${sidePart}.${userPart}`;
              },
            },
          ],
        },
      ],
    },
  ],
};
